import { FieldType, IValidation, MobxFieldControl } from './MobxFieldControl';
import { DateTimeService } from '@app/src/services/DateTimeService';
import getMinutes = DateTimeService.getMinutes;

export function isRequired(message?: string, required?: () => boolean) {
  return (target: any, name: string) =>
    addRequiredValidator(target, name, message, required);
}

export function isJSON(message?: string) {
  return (target: any, name: string) => addJSONValidator(target, name, message);
}

export function addRequiredValidator(
  target: any,
  name: string,
  message: string = 'validation.is.required',
  required: () => boolean = () => true
) {
  target[name].isMandatory = required();

  if (target[name].getValidator(message)) {
    removeValidatorByMessage(target, name, message);
  }

  if (target[name].isMandatory) {
    addValidator(target, name, {
      message,
      callback: value =>
        value !== void 0 &&
        value !== null &&
        value.toString().trim() !== '' &&
        value !== false
    });
  }
}

export function skipIf(callback: (target: any, value: any) => boolean) {
  return (target: any, name: string) =>
    addSkipValidator(target, name, callback);
}

export function skipCheckIf(callback: (target: any, value: any) => boolean) {
  return (target: any, name: string) =>
    addSkipCheckValidator(target, name, callback);
}

export function addEmailValidator(
  target: any,
  name: string,
  message: string = 'validation.is.email'
) {
  addValidator(target, name, {
    message,
    callback: value => {
      const regEx = /([\w.-]+@([\w-]+)\.+\w{2,})/;
      return !value || regEx.test(value);
    }
  });
}

export function addJSONValidator(
  target: any,
  name: string,
  message: string = 'validation.value.JSON'
) {
  addValidator(target, name, {
    message,
    callback: value => {
      try {
        const json = JSON.parse(value);
        return typeof json === 'object';
      } catch (e) {
        return false;
      }
    }
  });
}

export function isEmail(message?: string) {
  return (target: any, name: string) =>
    addEmailValidator(target, name, message);
}

export function isSameAs(fieldName: string, message: string) {
  return (target: any, name: string) => {
    addValidator(target, name, {
      message,
      callback: value => {
        return target[fieldName].value === value;
      }
    });
  };
}

export function addMoreThanValidator(
  target: any,
  name: string,
  fieldName: string,
  message: string = 'validation.isMoreThan',
  params?: any
) {
  addValidator(target, name, {
    message,
    params: params || {
      field1: fieldName,
      field2: name
    },
    callback: value => {
      switch (target[fieldName]._fieldType as FieldType) {
        case FieldType.DATE: {
          const field1 = new Date(target[fieldName].value);
          const field2 = new Date(value);
          return field1.getTime() < field2.getTime();
        }
        default:
          return target[fieldName].value < value;
      }
    }
  });
}

export function isMoreThan(fieldName: string, message?: string, params?: any) {
  return (target: any, name: string) =>
    addMoreThanValidator(target, name, fieldName, message, params);
}

export function isNotLessThan(
  fieldName: string,
  message: string = 'validation.isMoreThan',
  params?: any
) {
  return (target: any, name: string) => {
    addValidator(target, name, {
      message,
      params: params || {
        field1: fieldName,
        field2: name
      },
      callback: value => {
        switch (target[fieldName]._fieldType as FieldType) {
          case FieldType.DATE: {
            const field1 = new Date(target[fieldName].value);
            const field2 = new Date(value);
            return field1.getTime() <= field2.getTime();
          }
          default:
            return target[fieldName].value <= value;
        }
      }
    });
  };
}

export function isLessThan(
  fieldName: string,
  message: string = 'validation.isLessThan'
) {
  return (target: any, name: string) => {
    addValidator(target, name, {
      message,
      params: {
        fieldName1: fieldName,
        fieldName2: name
      },
      callback: value => {
        switch (target[fieldName]._fieldType as FieldType) {
          case FieldType.DATE: {
            const field1 = new Date(target[fieldName].value);
            const field2 = new Date(value);
            return field1.getTime() >= field2.getTime();
          }
          default:
            return target[fieldName].value >= value;
        }
      }
    });
  };
}

export function isLessThanNow(message: string = 'validation.isLessThanNow') {
  return (target: any, name: string) => {
    addValidator(target, name, {
      message,
      callback: value => {
        const field = new Date(value);
        return field.getTime() <= Date.now();
      }
    });
  };
}

export function isGreaterThanNow(
  delta: number = 0,
  message: string = 'validation.isGraterThanToday'
) {
  return (target: any, name: string) => {
    addValidator(target, name, {
      message,
      callback: value => {
        const currentDate = new Date();
        const currentDateWithDelta = currentDate.setMinutes(
          currentDate.getMinutes() + delta
        );
        setValidatorParams(target, name, message, {
          date: new Date(currentDateWithDelta).toLocaleString('en-US', {
            day: 'numeric',
            month: 'short',
            hour: 'numeric',
            minute: 'numeric'
          })
        });
        const field = new Date(value);
        return getMinutes(field.getTime()) >= getMinutes(currentDateWithDelta);
      }
    });
  };
}

export function addSameDateValidator(
  target: any,
  name: string,
  fieldName: string,
  message: string = 'validation.isSameDate',
  params?: any
) {
  addValidator(target, name, {
    message,
    params: params || {
      date: fieldName
    },
    callback: value => {
      switch (target[fieldName]._fieldType as FieldType) {
        case FieldType.DATE: {
          const field1 = new Date(target[fieldName].value);
          const field2 = new Date(value);
          return field1.getDate() === field2.getDate();
        }
        default:
          return false;
      }
    }
  });
}

export function addTimeRangeValidator(
  target: any,
  name: string,
  start?: string,
  end?: string,
  message = 'validation.is.notInRange',
  params = {}
) {
  addValidator(target, name, {
    message,
    params,
    callback: value => {
      if (!value) {
        return true;
      }
      const current = new Date(value);
      if (start) {
        const startDate = new Date(value);
        const [hours, minutes] = start.split(':');
        startDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
        if (current.getTime() < startDate.getTime()) {
          return false;
        }
      }
      if (end) {
        const endDate = new Date(value);
        const [hours, minutes] = end.split(':');
        endDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
        if (endDate.getTime() < current.getTime()) {
          return false;
        }
      }
      return true;
    }
  });
}

export function addLengthIsNotMoreThan(
  target: any,
  name: string,
  length: number = 120,
  message = 'validation.length.isNotMoreThan'
) {
  addValidator(target, name, {
    message,
    params: { length },
    callback: value => {
      if (value) {
        return value.toString().length <= length;
      }
      return true;
    }
  });
}

export function lengthIsNotMoreThan(length?: number, message?: string) {
  return (target: any, name: string) =>
    addLengthIsNotMoreThan(target, name, length, message);
}

export function lengthIsNotMoreThanIf(
  length: number = 120,
  condition: (target: any) => boolean,
  message = 'validation.length.isNotMoreThan'
) {
  return (target: any, name: string) => {
    addValidator(target, name, {
      message,
      params: { length },
      callback: value => {
        if (value && condition(target)) {
          return value.toString().length <= length;
        }
        return true;
      }
    });
  };
}

export function addValueIsNotLessThan(
  target: any,
  name: string,
  minValue: number = 0,
  message = 'validation.value.isNotLessThan'
) {
  addValidator(target, name, {
    message,
    params: { minValue },
    callback: value => {
      if (value || value === 0) {
        return minValue <= value;
      }
      return true;
    }
  });
}

export function valueIsNotLessThan(
  minValue: number = 0,
  message = 'validation.value.isNotLessThan'
) {
  return (target: any, name: string) =>
    addValueIsNotLessThan(target, name, minValue, message);
}

export function addValueIsNotMoreThan(
  target: any,
  name: string,
  maxValue: number = 100,
  message = 'validation.value.isNotMoreThan'
) {
  addValidator(target, name, {
    message,
    params: { maxValue },
    callback: value => {
      if (value || value === 0) {
        return value <= maxValue;
      }
      return true;
    }
  });
}

export function valueIsNotMoreThan(
  maxValue: number = 100,
  message = 'validation.value.isNotMoreThan'
) {
  return (target: any, name: string) =>
    addValueIsNotMoreThan(target, name, maxValue, message);
}

export function isPassword(message: string = 'validation.is.password') {
  return (target: any, name: string) => {
    addValidator(target, name, {
      message,
      callback: value => {
        const numberRegEx = /\d/;
        const upperRegEx = /[A-ZÄÖÜẞ\u0410-\u042F]/;
        const lowerRegEx = /[a-zäöüß\u0430-\u044F]/;
        return (
          !value ||
          (numberRegEx.test(value) &&
            upperRegEx.test(value) &&
            lowerRegEx.test(value) &&
            value.length > 9)
        );
      }
    });
  };
}

export function addValidExpressionValidator(
  target: any,
  name: string,
  regEx: RegExp,
  message: string = 'validation.is.expression'
) {
  addValidator(target, name, {
    message,
    callback: value => {
      return !value || regEx.test(value);
    }
  });
}

export function isValidExpression(
  regEx: RegExp,
  message: string = 'validation.is.expression'
) {
  return (target: any, name: string) =>
    addValidExpressionValidator(target, name, regEx, message);
}

export function addSkipValidator(
  target: { [key: string]: MobxFieldControl },
  name: string,
  callback: (target: any, value: any) => boolean
) {
  target[name].addSkipValidator((value: any) => callback(target, value));
}

export function addSkipCheckValidator(
  target: { [key: string]: MobxFieldControl },
  name: string,
  callback: (target: any, value: any) => boolean
) {
  target[name].addSkipCheckValidator((value: any) => callback(target, value));
}

function addValidator(
  target: { [key: string]: MobxFieldControl },
  name: string,
  validator: IValidation
) {
  target[name].addValidator(validator);
}

function removeValidatorByMessage(
  target: { [key: string]: MobxFieldControl },
  name: string,
  message: string
) {
  target[name].removeValidator(message);
}

function setValidatorParams(
  target: { [key: string]: MobxFieldControl },
  name: string,
  message: string,
  params: { [key: string]: any }
) {
  const validator = target[name].getValidator(message);
  if (validator) {
    validator.params = params;
  }
}
